import React, { useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import { Rnd } from "react-rnd";
import FormComponent from "./FormComponent";

// Dimensiones base del documento (se adaptarán al contenedor)
const BASE_WIDTH = 895;
const BASE_HEIGHT = 1200;
const GRID_SIZE_PERCENT = 2; // 2% del ancho para snap
const GUIDE_COLOR = "rgba(33, 150, 243, 0.3)"; // azul sutil
const HEADER_HEIGHT_PERCENT = 8; // 8% de la altura

function snapToGrid(val, containerWidth) {
  const gridSize = (containerWidth * GRID_SIZE_PERCENT) / 100;
  return Math.round(val / gridSize) * gridSize;
}

function isOverlapping(rect, others, ignoreId) {
  return others.some((comp) => {
    if (comp.id === ignoreId) return false;
    if (comp.type === "formHeader") return false; // Ignorar cabecera
    const c = comp.rndPosition || { x: 0, y: 0, width: 40, height: 10 }; // Valores por defecto más razonables
    return !(
      rect.x + rect.width <= c.x ||
      rect.x >= c.x + c.width ||
      rect.y + rect.height <= c.y ||
      rect.y >= c.y + c.height
    );
  });
}

// Encuentra líneas de alineación (bordes y centros)
function getSnapGuides(movingRect, components, ignoreId) {
  const guides = [];
  const tolerance = 6; // px para snap
  const moving = {
    left: movingRect.x,
    right: movingRect.x + movingRect.width,
    top: movingRect.y,
    bottom: movingRect.y + movingRect.height,
    vcenter: movingRect.x + movingRect.width / 2,
    hcenter: movingRect.y + movingRect.height / 2,
  };
  components.forEach((comp) => {
    if (comp.id === ignoreId) return;
    if (comp.type === "formHeader") return; // Ignorar cabecera
    const pos = comp.rndPosition || { x: 0, y: 0, width: 40, height: 10 }; // Valores por defecto más razonables
    const target = {
      left: pos.x,
      right: pos.x + pos.width,
      top: pos.y,
      bottom: pos.y + pos.height,
      vcenter: pos.x + pos.width / 2,
      hcenter: pos.y + pos.height / 2,
    };
    // Comparar cada borde/centro
    Object.entries(moving).forEach(([mkey, mval]) => {
      Object.entries(target).forEach(([tkey, tval]) => {
        if (mkey === tkey && Math.abs(mval - tval) <= tolerance) {
          guides.push({
            orientation: mkey.includes("center")
              ? "center"
              : ["left", "right"].includes(mkey)
                ? "vertical"
                : "horizontal",
            value: tval,
          });
        }
      });
    });
  });
  return guides;
}

const RndCanvas = ({
  components,
  onComponentUpdate,
  onComponentDelete,
  selectedComponent,
  onComponentSelect,
  onDrop,
  isPreviewMode,
  formHeader,
  onHeaderUpdate,
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [snapGuides, setSnapGuides] = useState([]);
  const [canvasDimensions, setCanvasDimensions] = useState({ width: BASE_WIDTH, height: BASE_HEIGHT });
  const canvasRef = useRef(null);

  // Mantener dimensiones fijas del canvas
  React.useEffect(() => {
    // Usar dimensiones fijas basadas en BASE_WIDTH y BASE_HEIGHT
    setCanvasDimensions({
      width: BASE_WIDTH,
      height: BASE_HEIGHT,
    });
  }, []);

  // Para nuevos componentes: buscar espacio libre
  const findFreeSpot = (width, height) => {
    const gridSize = (canvasDimensions.width * GRID_SIZE_PERCENT) / 100;
    for (let y = 0; y < canvasDimensions.height; y += gridSize) {
      for (let x = 0; x < canvasDimensions.width; x += gridSize) {
        const rect = { x, y, width, height };
        if (!isOverlapping(rect, components)) {
          return { x, y };
        }
      }
    }
    return { x: 0, y: 0 };
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    setSnapGuides([]);
    const componentData = e.dataTransfer.getData("application/json");
    if (componentData) {
      const component = JSON.parse(componentData);
      if (!canvasRef.current) return;
      const rect = canvasRef.current.getBoundingClientRect();
      const x = snapToGrid(e.clientX - rect.left, canvasDimensions.width);
      const y = snapToGrid(e.clientY - rect.top, canvasDimensions.width);

      // Usar los tamaños definidos en el componente, no valores por defecto
      const width = component.rndPosition?.width
        ? (component.rndPosition.width / 100) * canvasDimensions.width
        : canvasDimensions.width * 0.2;
      const height = component.rndPosition?.height
        ? (component.rndPosition.height / 100) * canvasDimensions.height
        : 60;
      // Si ya existe, solo mover
      const exists = components.some((c) => c.id === component.id);
      let pos = { x, y };
      if (!exists) {
        // Buscar espacio libre si está ocupado
        if (isOverlapping({ x, y, width, height }, components)) {
          pos = findFreeSpot(width, height);
        }
      } else {
        // Si está ocupado, buscar otro
        if (isOverlapping({ x, y, width, height }, components, component.id)) {
          pos = findFreeSpot(width, height);
        }
      }
      if (exists) {
        // Convertir a porcentajes
        const percentX = (pos.x / canvasDimensions.width) * 100;
        const percentY = (pos.y / canvasDimensions.height) * 100;
        const percentWidth = (width / canvasDimensions.width) * 100;
        const percentHeight = (height / canvasDimensions.height) * 100;
        onComponentUpdate(component.id, {
          rndPosition: {
            x: percentX,
            y: percentY,
            width: percentWidth,
            height: percentHeight,
          },
        });
      } else {
        // Convertir a porcentajes
        const percentX = (pos.x / canvasDimensions.width) * 100;
        const percentY = (pos.y / canvasDimensions.height) * 100;
        const percentWidth = (width / canvasDimensions.width) * 100;
        const percentHeight = (height / canvasDimensions.height) * 100;
        onDrop({
          ...component,
          rndPosition: {
            x: percentX,
            y: percentY,
            width: percentWidth,
            height: percentHeight,
          },
        });
      }
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    setSnapGuides([]);
  };

  const handleComponentClick = (component) => {
    if (!isPreviewMode) {
      onComponentSelect(component);
    }
  };

  const handleResizeStop = (id, d, ref, delta, position) => {
    const width = Math.min(snapToGrid(ref.offsetWidth, canvasDimensions.width), canvasDimensions.width);
    const height = snapToGrid(ref.offsetHeight, canvasDimensions.width);
    const x = snapToGrid(position.x, canvasDimensions.width);
    const y = snapToGrid(position.y, canvasDimensions.width);
    // Evitar superposición
    if (
      !isOverlapping({ x, y, width, height }, components, id) &&
      x + width <= canvasDimensions.width &&
      y + height <= canvasDimensions.height &&
      x >= 0 &&
      y >= 0
    ) {
      // Convertir a porcentajes
      const percentX = (x / canvasDimensions.width) * 100;
      const percentY = (y / canvasDimensions.height) * 100;
      const percentWidth = (width / canvasDimensions.width) * 100;
      const percentHeight = (height / canvasDimensions.height) * 100;
      onComponentUpdate(id, {
        rndPosition: {
          x: percentX,
          y: percentY,
          width: percentWidth,
          height: percentHeight,
        },
      });
    }
    setSnapGuides([]);
  };

  const handleDragStop = (id, d, position) => {
    const component = components.find((c) => c.id === id);
    const rndPos = component?.rndPosition || { width: 40, height: 10 }; // Valores por defecto más razonables
    const width = (rndPos.width / 100) * canvasDimensions.width;
    const height = (rndPos.height / 100) * canvasDimensions.height;
    const x = snapToGrid(position.x, canvasDimensions.width);
    const y = snapToGrid(position.y, canvasDimensions.width);
    if (
      !isOverlapping({ x, y, width, height }, components, id) &&
      x + width <= canvasDimensions.width &&
      y + height <= canvasDimensions.height &&
      x >= 0 &&
      y >= 0
    ) {
      // Convertir a porcentajes
      const percentX = (x / canvasDimensions.width) * 100;
      const percentY = (y / canvasDimensions.height) * 100;
      const percentWidth = (width / canvasDimensions.width) * 100;
      const percentHeight = (height / canvasDimensions.height) * 100;
      onComponentUpdate(id, {
        rndPosition: {
          x: percentX,
          y: percentY,
          width: percentWidth,
          height: percentHeight,
        },
      });
    }
    setSnapGuides([]);
  };

  // Mostrar guías de alineación al mover/redimensionar
  const handleDrag = (id, d) => {
    const component = components.find((c) => c.id === id);
    const rndPos = component?.rndPosition || { width: 40, height: 10 }; // Valores por defecto más razonables
    const width = (rndPos.width / 100) * canvasDimensions.width;
    const height = (rndPos.height / 100) * canvasDimensions.height;
    const x = snapToGrid(d.x, canvasDimensions.width);
    const y = snapToGrid(d.y, canvasDimensions.width);
    setSnapGuides(getSnapGuides({ x, y, width, height }, components, id));
  };
  const handleResize = (id, d, ref, delta, position) => {
    const width = Math.min(snapToGrid(ref.offsetWidth, canvasDimensions.width), canvasDimensions.width);
    const height = snapToGrid(ref.offsetHeight, canvasDimensions.width);
    const x = snapToGrid(position.x, canvasDimensions.width);
    const y = snapToGrid(position.y, canvasDimensions.width);
    setSnapGuides(getSnapGuides({ x, y, width, height }, components, id));
  };

  return (
    <Box sx={{ width: "100%", height: "100%", overflow: "auto", p: 0, display: "flex", flexDirection: "column" }}>
      <Box
        ref={canvasRef}
        sx={{
          width: "100%",
          height: `${canvasDimensions.height}px`,
          bgcolor: "white",
          border: isDragOver ? "2px dashed #90caf9" : "1px solid #e0e0e0",
          boxShadow: isDragOver ? "0 0 0 4px #e3f2fd" : "0 2px 8px rgba(0,0,0,0.10)",
          transition: "all 0.2s ease",
          position: "relative",
          borderRadius: 2,
          overflow: "hidden",
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          borderBottomLeftRadius: 16,
          borderBottomRightRadius: 16,
        }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={(e) => {
          // Solo deselecciona si el click es directamente en el canvas, no en un componente
          if (e.target === canvasRef.current) {
            onComponentSelect(null);
          }
        }}
      >
        {/* Grilla de alineamiento visual */}
        {(() => {
          const gridSize = (canvasDimensions.width * GRID_SIZE_PERCENT) / 100;
          return (
            <>
              {Array.from({ length: Math.floor(canvasDimensions.width / gridSize) }).map((_, i) => (
                <Box
                  key={i}
                  sx={{
                    position: "absolute",
                    left: i * gridSize,
                    top: 0,
                    width: 1,
                    height: "100%",
                    bgcolor: "white",
                    opacity: 0.15,
                    pointerEvents: "none",
                  }}
                />
              ))}
              {Array.from({ length: Math.floor(canvasDimensions.height / gridSize) }).map((_, i) => (
                <Box
                  key={i}
                  sx={{
                    position: "absolute",
                    top: i * gridSize,
                    left: 0,
                    height: 1,
                    width: "100%",
                    bgcolor: "white",
                    opacity: 0.15,
                    pointerEvents: "none",
                  }}
                />
              ))}
            </>
          );
        })()}
        {/* Guías de alineación */}
        {snapGuides.map((guide, idx) =>
          guide.orientation === "vertical" ? (
            <Box
              key={idx}
              sx={{
                position: "absolute",
                left: guide.value,
                top: 0,
                width: 2,
                height: "100%",
                bgcolor: GUIDE_COLOR,
                zIndex: 100,
                pointerEvents: "none",
              }}
            />
          ) : (
            <Box
              key={idx}
              sx={{
                position: "absolute",
                top: guide.value,
                left: 0,
                height: 2,
                width: "100%",
                bgcolor: GUIDE_COLOR,
                zIndex: 100,
                pointerEvents: "none",
              }}
            />
          )
        )}
        {/* Renderizar todos los componentes, incluido el formHeader, usando Rnd y FormComponent */}
        {components.map((component) => {
          const isHeader = component.type === "formHeader";
          const headerHeight = (canvasDimensions.height * HEADER_HEIGHT_PERCENT) / 100;
          const gridSize = (canvasDimensions.width * GRID_SIZE_PERCENT) / 100;

          // Convertir porcentajes a píxeles para renderizar
          let pos;
          if (isHeader) {
            pos = { x: 0, y: 0, width: canvasDimensions.width, height: headerHeight };
          } else {
            const rndPos = component.rndPosition || { x: 0, y: 0, width: 40, height: 10 }; // Valores por defecto más razonables
            pos = {
              x: (rndPos.x / 100) * canvasDimensions.width,
              y: (rndPos.y / 100) * canvasDimensions.height,
              width: (rndPos.width / 100) * canvasDimensions.width,
              height: (rndPos.height / 100) * canvasDimensions.height,
            };
          }

          return (
            <Rnd
              key={component.id}
              size={{ width: Math.min(pos.width, canvasDimensions.width), height: pos.height }}
              position={{ x: pos.x, y: pos.y }}
              minWidth={60}
              minHeight={40}
              bounds="parent"
              enableResizing={!isHeader && !isPreviewMode}
              disableDragging={isHeader || isPreviewMode}
              grid={[gridSize, gridSize]}
              onDragStop={(e, d) => !isHeader && handleDragStop(component.id, d, d)}
              onResizeStop={(e, d, ref, delta, position) =>
                !isHeader && handleResizeStop(component.id, d, ref, delta, position)
              }
              onDrag={(e, d) => !isHeader && handleDrag(component.id, d)}
              onResize={(e, d, ref, delta, position) =>
                !isHeader && handleResize(component.id, d, ref, delta, position)
              }
              style={{ zIndex: selectedComponent?.id === component.id ? 10 : 1 }}
              maxWidth={canvasDimensions.width}
              disableUserSelectHack={true}
            >
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  border: selectedComponent?.id === component.id ? "2px solid" : "1px solid transparent",
                  borderColor: selectedComponent?.id === component.id ? "primary.main" : "transparent",
                  borderRadius: 2,
                  p: selectedComponent?.id === component.id ? 1 : 0,
                  cursor: isPreviewMode ? "default" : "pointer",
                  bgcolor: "white",
                  position: "relative",
                  "&:hover": {
                    borderColor: isPreviewMode ? "transparent" : "primary.light",
                    bgcolor: isPreviewMode ? "transparent" : "action.hover",
                  },
                  transition: "all 0.2s ease",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleComponentClick(component);
                }}
              >
                {/* Componente visual */}
                <Box
                  sx={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <FormComponent
                    component={component}
                    mode="edit"
                    onUpdate={(updates) => onComponentUpdate(component.id, updates)}
                    isSelected={selectedComponent?.id === component.id}
                    onDelete={() => onComponentDelete(component.id)}
                  />
                </Box>
              </Box>
            </Rnd>
          );
        })}
        {/* Mensaje cuando no hay componentes */}
        {components.length === 0 && (
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              color: "text.secondary",
            }}
          >
            <Typography variant="h6" sx={{ mb: 2 }}>
              No hay componentes en el formulario
            </Typography>
            <Typography variant="body2">Arrastra componentes desde la barra lateral para comenzar</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default RndCanvas;
